// Las tres piezas con las que una pantalla que entra por enlace
// (`id_token_hint`) le dice al usuario sobre qué cuenta está parado. La lógica
// de cuándo va cada una vive en lib/account-guard.ts.
//
//   <AccountLine>            — "Cuenta: ju***@gmail.com · No es mi cuenta".
//                              Permanente: nadie debería pagar sin haberla visto.
//   <AccountConfirmScreen>   — paso intermedio antes de cualquier cambio cuando
//                              la cuenta solo la afirma el enlace.
//   <AccountMismatchScreen>  — bloqueo: el enlace es de otra cuenta. No muestra
//                              ningún dato de esa cuenta (no lo tenemos, a
//                              propósito: el backend no lo manda).

import type { ReactNode } from 'react';
import AuthShell from '@/components/AuthShell';
import { btn } from '@/lib/button-styles';
import type { MismatchReason } from '@/lib/account-guard';

export function AccountLine({
  hint,
  onSwitch,
}: {
  hint: string | null;
  /** Sin handler no se ofrece el cambio (ej. con un pago abierto en el widget). */
  onSwitch?: (() => void) | null;
}): JSX.Element | null {
  if (!hint) return null;
  return (
    <div
      role="group"
      aria-label="Cuenta sobre la que estás operando"
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-text"
    >
      <span className="min-w-0 break-all">
        <span className="text-text-muted">Cuenta: </span>
        <strong className="font-semibold">{hint}</strong>
      </span>
      {onSwitch ? (
        <button type="button" onClick={onSwitch} className={btn('ghost', 'sm', 'underline -mr-2')}>
          No es mi cuenta
        </button>
      ) : null}
    </div>
  );
}

export function AccountConfirmScreen({
  hint,
  onConfirm,
  onSwitch,
  onBack,
}: {
  hint: string | null;
  onConfirm: () => void;
  onSwitch: () => void;
  /** Volver a mirar sin operar. */
  onBack: () => void;
}): JSX.Element {
  return (
    <AuthShell title="¿Es tu cuenta?" subtitle="Confirmalo antes de continuar.">
      <div className="space-y-4">
        <p role="alert" className="rounded-lg border border-border p-4 text-sm text-text">
          {hint ? (
            <>
              Vas a operar sobre la cuenta <strong className="break-all font-semibold">{hint}</strong>.
              ¿Es tu cuenta?
            </>
          ) : (
            <>
              Entraste con un enlace y no pudimos comprobar que la cuenta sea la tuya. Si no lo
              abriste vos desde la aplicación, iniciá sesión con tu cuenta.
            </>
          )}
        </p>
        <button type="button" onClick={onConfirm} className={btn('primary', 'md', 'w-full')}>
          Sí, es mi cuenta
        </button>
        <button type="button" onClick={onSwitch} className={btn('secondary', 'md', 'w-full')}>
          No, iniciar sesión con otra cuenta
        </button>
        <div className="text-center">
          <button type="button" onClick={onBack} className={btn('ghost', 'sm')}>
            Volver sin hacer cambios
          </button>
        </div>
      </div>
    </AuthShell>
  );
}

const MISMATCH_COPY: Record<MismatchReason, { title: string; body: string }> = {
  other_account: {
    title: 'Este enlace es de otra cuenta',
    body:
      'El enlace con el que entraste corresponde a una cuenta distinta de la que usaste en este ' +
      'dispositivo. Para tu seguridad no podemos continuar con ese enlace.',
  },
  link_used_elsewhere: {
    title: 'Este enlace ya se usó en otro dispositivo',
    body:
      'Cada enlace sirve en un solo navegador. Para tu seguridad no podemos continuar con este: ' +
      'iniciá sesión con tu cuenta para seguir.',
  },
  link_expired: {
    title: 'Este enlace venció',
    body: 'Pasó demasiado tiempo desde que se generó el enlace. Iniciá sesión con tu cuenta para seguir.',
  },
};

export function AccountMismatchScreen({
  onLogin,
  flowExit,
  reason = 'other_account',
}: {
  onLogin: () => void;
  flowExit?: ReactNode;
  reason?: MismatchReason;
}): JSX.Element {
  const copy = MISMATCH_COPY[reason];
  return (
    <AuthShell title={copy.title} flowExit={flowExit}>
      <div className="space-y-4">
        <p role="alert" className="rounded-lg border border-error/30 bg-error/10 p-4 text-sm text-text">
          {copy.body}
        </p>
        <button type="button" onClick={onLogin} className={btn('primary', 'md', 'w-full')}>
          Iniciar sesión con mi cuenta
        </button>
      </div>
    </AuthShell>
  );
}
